import { clamp, type NormalizedLandmark } from '../types/geometry.js';
import type { Confidence } from '../types/metrics.js';
import { LANDMARKS, pick } from './landmarks.js';
import { round, safeDiv } from './statistics.js';

/**
 * Head-top estimation.
 *
 * This is the single most error-prone measurement in the whole validator, so it
 * gets its own module, its own confidence, and an explicit list of hard cases it
 * refuses to guess about:
 *
 *   dark hair on a dark background   → no silhouette edge found; low contrast
 *   light hair on a white background → contrast near the noise floor
 *   long hair / volume / headwear     → the silhouette top is the outer contour
 *   partially covered head            → landmarks missing ⇒ no measurement at all
 *
 * The estimator always errs towards *under*-estimating the head, because an
 * over-estimated head can silently turn a "head too small" photo into a pass.
 */

export interface HeadTopEstimatorOptions {
  /** Mesh height multiplier that reaches the top of the hair. */
  meshFactor: number;
  /** Assumed distance from the eye line to the top of the head, as a share of head height. */
  eyeLineShare: number;
  /** Blend weight of the mesh estimator, 0..1. 0.5 = equal trust. */
  meshWeight: number;
}

/**
 * `meshFactor` = head height / (forehead → chin).
 *
 * Landmark 10 sits on the forehead skin, and *where* exactly depends on the face:
 * on the construction-based calibration drawings (a flat vector head with no hair
 * above the mesh) the factor 1.32 reproduces the drawn 60 % head exactly, while on
 * 20 real photographs the same factor under-estimates the head height on every
 * frame (MAE 5.02 % of frame height, worst 9.60 %, 16/20 outside the 2.5 %
 * tolerance). Raising it to 1.55 fixes the photographs (MAE 1.60 %, worst 3.97 %,
 * 5/20 outside) but breaks the drawings (60 % → 65.2 %, `tests/geometry` red), so
 * the constant stays at the value that matches the construction ground truth.
 * The real-photo residual is tracked in `tests/geometry/real/reports/` and needs a
 * rule that uses more evidence than one multiplier — not a global constant.
 * `?debug=1` publishes the three estimators so the next attempt stays checkable.
 *
 * `eyeLineShare` = (eye line → chin) / head height. On a canonically proportioned
 * head the eyes sit at the vertical midpoint, so the eye line to chin distance is
 * about half the head height and `head ≈ 2 × (chin − eye line)`. This estimator
 * does not depend on hair at all, which is why it is the safety net when the
 * silhouette cannot separate hair from the background.
 */
export const DEFAULT_HEAD_TOP_OPTIONS: HeadTopEstimatorOptions = {
  meshFactor: 1.32,
  eyeLineShare: 0.5,
  meshWeight: 0.5,
};

/**
 * Quantile of the accepted column boundaries used as the head top.
 *
 * The scan walks *upwards* from inside the head, so a column that fails to see a
 * low-contrast top edge keeps walking into the soft hair and stops **below** the
 * true top: the per-column error is one-sided, never upwards. The median is
 * therefore biased downwards whenever several columns miss the edge — on
 * `19.png` (dark hair on a dark background, edge contrast 19.5 luma against a
 * tolerance of 14) four of six columns locked onto internal edges, the median
 * landed 39 px below the manual ground truth, while the topmost boundaries
 * (78–80 px) were right. A lower quantile takes that upper envelope while still
 * tolerating one spurious column; a strict minimum does not — in the offline
 * check over all 20 real photographs it broke `06.png` (−2.15 % H) and
 * `08.png` (−2.27 % H), whereas p33 leaves every frame inside tolerance.
 */
const COLUMN_BOUNDARY_QUANTILE = 0.33;

/** Linear-interpolated quantile; `median` is the `0.5` case. */
const lowerQuantile = (values: readonly number[], p: number): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 1) return sorted[0] as number;
  const position = (sorted.length - 1) * clamp(p, 0, 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower] as number;
  const weight = position - lower;
  return (sorted[lower] as number) * (1 - weight) + (sorted[upper] as number) * weight;
};

/** Plausibility band for a head height, relative to the two estimators. */
const MIN_HEAD_OVER_FACE_MESH = 1.08;
const MAX_HEAD_OVER_CHIN_TO_EYE = 2.2;
/** A head is never much taller than the forehead→chin mesh (hair volume has limits). */
const MAX_HEAD_OVER_FACE_MESH = 1.8;

export interface HeadTopEstimate {
  /** Normalized y of the estimated top of the head, including hair. */
  headTopY: number;
  /** Normalized head height (headTopY .. chinY). */
  headHeight: number;
  source: 'mesh-extrapolation' | 'silhouette' | 'unavailable';
  confidence: Confidence;
  /**
   * The three numbers the estimate is built from, in normalized image units:
   * the face mesh (forehead → chin), the hair-inclusive extrapolation of it, and
   * the hair-independent eye-line estimate.
   */
  estimators: { mesh: number; eyeLine: number; faceMesh: number };
  /**
   * The plausibility band the blended estimate was clamped into, plus which end
   * was hit. Diagnostic only — it never changes the number, it explains it.
   */
  bounds?: {
    lowerBound: number;
    upperBound: number;
    clamped: 'none' | 'lower' | 'upper';
  };
  /** What happened to the silhouette refinement, and why (diagnostic only). */
  silhouette?: {
    decision: 'accepted' | 'rejected' | 'not-run';
    reason?: string;
    headHeight?: number;
  };
  /** Normalized y of the chin the estimate was measured to. */
  chinY: number;
  /** Human-readable hard-case notes; surfaced in the debug/detail panel. */
  notes: string[];
}

const UNAVAILABLE: HeadTopEstimate = {
  headTopY: 0,
  headHeight: 0,
  source: 'unavailable',
  confidence: { value: 0, reason: 'Не удалось определить положение макушки.' },
  estimators: { mesh: 0, eyeLine: 0, faceMesh: 0 },
  chinY: 0,
  notes: ['landmarks-missing'],
};

/**
 * Extrapolates the top of the head from the mesh and from the eye line.
 * Both estimators return a head *height*; the smaller (more conservative) one
 * decides the head top.
 */
export const estimateHeadTop = (
  landmarks: readonly NormalizedLandmark[],
  eyeLineY: number,
  options: HeadTopEstimatorOptions = DEFAULT_HEAD_TOP_OPTIONS,
): HeadTopEstimate => {
  const chin = pick(landmarks, LANDMARKS.chinBottom);
  const forehead = pick(landmarks, LANDMARKS.foreheadTopCenter);
  if (!chin || !forehead) return UNAVAILABLE;

  const meshHeight = chin.y - forehead.y;
  if (meshHeight <= 0.01) return UNAVAILABLE;

  const chinToEye = chin.y - eyeLineY;
  if (chinToEye <= 0.01) return UNAVAILABLE;

  const meshHeightEstimate = meshHeight * options.meshFactor;
  const eyeLineEstimate = safeDiv(chinToEye, Math.max(0.05, 1 - options.eyeLineShare), 0);

  const weight = clamp(options.meshWeight, 0, 1);
  const blended = meshHeightEstimate * weight + eyeLineEstimate * (1 - weight);
  /**
   * Plausibility band instead of the old "min(estimators) × 1.12" ceiling.
   *
   * The old cap punished people with hair volume: the mesh estimator cannot see
   * hair, so a low mesh value dragged a correct measurement down and turned a
   * 60 % head into a 44 % one. The head must be at least a little taller than the
   * face mesh (the mesh ends at the forehead) and cannot be absurdly tall
   * relative to the eye line — that is the honest range.
   */
  const lowerBound = meshHeight * MIN_HEAD_OVER_FACE_MESH;
  const upperBound = chinToEye * MAX_HEAD_OVER_CHIN_TO_EYE;
  const clampedHeadHeight = clamp(blended, lowerBound, Math.max(lowerBound, upperBound));
  const headHeight = clampedHeadHeight;
  const clamped: 'none' | 'lower' | 'upper' =
    clampedHeadHeight === blended
      ? 'none'
      : clampedHeadHeight === Math.max(lowerBound, upperBound)
        ? 'upper'
        : 'lower';

  const relativeGap =
    Math.abs(meshHeightEstimate - eyeLineEstimate) /
    Math.max(meshHeightEstimate, eyeLineEstimate, 1e-6);
  const agreement = 1 - clamp(relativeGap, 0, 1);
  /**
   * Two estimators that disagree by more than a third describe different heads:
   * the landmark set is inconsistent (wrong chin, missing mesh, absurd pose) and
   * the honest answer is "I do not know", not a verdict. That is why a strong
   * disagreement caps the confidence below the usual uncertainty threshold.
   */
  const stronglyDisagree = relativeGap > 0.3;
  const confidenceValue = stronglyDisagree
    ? Math.min(0.4, clamp(0.35 + agreement * 0.45, 0, 0.8))
    : clamp(0.35 + agreement * 0.45, 0, 0.8);

  const notes: string[] = [];
  if (clamped !== 'none') {
    notes.push(clamped === 'lower' ? 'guard-lower-bound' : 'guard-upper-bound');
  }
  if (relativeGap > 0.25) {
    notes.push('estimator-disagreement');
  }
  if (stronglyDisagree) {
    notes.push('estimators-inconsistent');
  }

  return {
    headTopY: chin.y - headHeight,
    headHeight,
    source: 'mesh-extrapolation',
    chinY: chin.y,
    confidence: {
      value: round(confidenceValue, 2),
      reason:
        relativeGap > 0.25
          ? 'Оценки высоты головы расходятся: граница волос определена приблизительно.'
          : undefined,
    },
    estimators: {
      mesh: round(meshHeightEstimate, 4),
      eyeLine: round(eyeLineEstimate, 4),
      faceMesh: round(meshHeight, 4),
    },
    bounds: {
      lowerBound: round(lowerBound, 4),
      upperBound: round(Math.max(lowerBound, upperBound), 4),
      clamped,
    },
    silhouette: { decision: 'not-run' },
    notes,
  };
};

export interface SilhouetteRefineInput {
  /** Luma plane of the analysis buffer. */
  luma: Uint8ClampedArray;
  width: number;
  height: number;
  /** Normalized x of the column to scan (forehead centre). */
  columnX: number;
  /** Normalized y to start scanning upwards from (the mesh head top). */
  startY: number;
  /** Mean luma of the background (frame ring average), 0..255. */
  backgroundLuma: number;
  /**
   * Second background reference: the luma of the top band of the frame, which is
   * where the background above the head actually is. A pixel counts as background
   * when it is close to *either* reference — the ring average alone is dragged
   * down by dark clothing at the bottom of a portrait and made the scan blind.
   */
  backgroundLumaAlt?: number;
  /** Minimum luma difference that counts as "subject rather than background". */
  tolerance?: number;
  /**
   * Minimum local gradient magnitude that counts as structure. Hair has texture
   * even when its average luma matches the background (dark hair on a dark wall),
   * so structure is a second, independent way to recognise the subject.
   */
  structureThreshold?: number;
  /**
   * Estimated sensor/JPEG noise sigma in luma units. Raises the contrast
   * threshold so that grain is never mistaken for a hair boundary.
   */
  noiseSigma?: number;
  /** How far up we are willing to look, as a share of image height. */
  maxSearch?: number;
  /** Number of columns scanned around `columnX`. */
  columns?: number;
}

export interface SilhouetteRefineResult {
  found: boolean;
  headTopY: number;
  confidence: number;
  contrast: number;
  columnsUsed: number;
  /** True when the scan ran to the top edge without meeting the background. */
  reachedImageTop: boolean;
  /** Machine-readable hard cases, surfaced in the debug panel. */
  notes: string[];
}

const notFound = (startY: number, notes: string[], reachedImageTop = false): SilhouetteRefineResult => ({
  found: false,
  headTopY: startY,
  confidence: 0,
  contrast: 0,
  columnsUsed: 0,
  reachedImageTop,
  notes,
});

/**
 * Refines the mesh extrapolation by walking up the forehead columns until the
 * subject silhouette meets the background.
 *
 * This is what makes "top of the hair" measurable on real photos instead of
 * guessed: hair against a wall is a large luma step. It refuses to guess when
 * the background is dark, busy, or when no consistent edge is found.
 */
export const refineHeadTopFromSilhouette = (
  input: SilhouetteRefineInput,
): SilhouetteRefineResult => {
  const baseTolerance = input.tolerance ?? 14;
  // Grain must never look like a hairline edge.
  const tolerance = Math.max(baseTolerance, (input.noiseSigma ?? 0) * 3);
  const structureThreshold = Math.max(input.structureThreshold ?? 0, (input.noiseSigma ?? 0) * 4);
  const maxSearch = input.maxSearch ?? 0.25;
  const columnCount = input.columns ?? 7;
  const { luma, width, height } = input;
  if (width <= 0 || height <= 0 || luma.length < width * height) {
    return notFound(input.startY, ['invalid-buffer']);
  }

  const startPixelY = clamp(Math.round(input.startY * height), 0, height - 1);
  const searchLimit = Math.max(1, Math.round(maxSearch * height));
  const spread = Math.max(2, Math.round(width * 0.03));
  const centreX = clamp(Math.round(input.columnX * width), 0, width - 1);

  const boundaries: number[] = [];
  const contrasts: number[] = [];
  let reachedImageTopColumns = 0;

  /** Local structure: the strongest luma step in a 3×3 window. */
  const localStructure = (x: number, y: number): number => {
    const value = luma[y * width + x] as number;
    let strongest = 0;
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
        strongest = Math.max(strongest, Math.abs((luma[ny * width + nx] as number) - value));
      }
    }
    return strongest;
  };

  for (let k = 0; k < columnCount; k += 1) {
    const offset = columnCount === 1 ? 0 : Math.round(((k / (columnCount - 1)) * 2 - 1) * spread);
    const x = clamp(centreX + offset, 0, width - 1);
    let runOfBackground = 0;
    let lastSubjectY = -1;
    let bestStep = 0;
    let boundaryY = -1;
    let reachedTop = true;

    for (let step = 0; step <= searchLimit; step += 1) {
      const y = startPixelY - step;
      if (y < 0) break;
      const value = luma[y * width + x] as number;
      const difference =
        input.backgroundLumaAlt === undefined
          ? Math.abs(value - input.backgroundLuma)
          : Math.min(
              Math.abs(value - input.backgroundLuma),
              Math.abs(value - input.backgroundLumaAlt),
            );
      const structured = structureThreshold > 0 && localStructure(x, y) > structureThreshold;
      if (difference > tolerance || structured) {
        runOfBackground = 0;
        lastSubjectY = y;
        if (difference > bestStep) bestStep = difference;
      } else {
        runOfBackground += 1;
        // Three consecutive background pixels above the subject = the real edge.
        if (runOfBackground >= 3 && lastSubjectY >= 0) {
          boundaryY = lastSubjectY;
          reachedTop = false;
          break;
        }
      }
    }

    if (reachedTop && boundaryY < 0) reachedImageTopColumns += 1;

    if (boundaryY >= 0 && bestStep >= tolerance) {
      boundaries.push(boundaryY / height);
      contrasts.push(bestStep);
    }
  }

  const reachedImageTop = reachedImageTopColumns > columnCount / 2;
  const minimumColumns = Math.max(2, Math.ceil(columnCount / 2));

  if (boundaries.length < minimumColumns) {
    const notes: string[] = [];
    if (reachedImageTop) {
      notes.push('no-background-above');
      notes.push('dark-subject-on-dark-background');
    } else {
      notes.push('no-consistent-edge');
    }
    if (tolerance > baseTolerance) notes.push('high-noise-tolerance');
    return notFound(input.startY, notes, reachedImageTop);
  }

  // Upper envelope of the column boundaries: see COLUMN_BOUNDARY_QUANTILE.
  const headTopY = lowerQuantile(boundaries, COLUMN_BOUNDARY_QUANTILE);
  const meanContrast = contrasts.reduce((sum, value) => sum + value, 0) / contrasts.length;
  const spreadOfBoundaries = Math.max(...boundaries) - Math.min(...boundaries);
  const agreement = 1 - clamp(spreadOfBoundaries / 0.05, 0, 1);
  const confidence = clamp(
    (boundaries.length / columnCount) * 0.5 + agreement * 0.3 + clamp(meanContrast / 80, 0, 1) * 0.2,
    0,
    0.95,
  );

  const notes: string[] = ['silhouette-edge-found'];
  if (meanContrast < 30) notes.push('weak-contrast');
  if (agreement < 0.6) notes.push('columns-disagree');
  if (tolerance > baseTolerance) notes.push('high-noise-tolerance');

  return {
    found: true,
    headTopY,
    confidence: round(confidence, 2),
    contrast: round(meanContrast, 1),
    columnsUsed: boundaries.length,
    reachedImageTop: false,
    notes,
  };
};

/**
 * Merges a successful silhouette measurement into the extrapolated estimate.
 * A real observed edge always beats an extrapolation; a failed scan keeps the
 * extrapolation but records *why* it failed.
 */
export const mergeSilhouetteEstimate = (
  estimate: HeadTopEstimate,
  silhouette: SilhouetteRefineResult,
  options: { meshTopY?: number } = {},
): HeadTopEstimate => {
  if (!silhouette.found || silhouette.confidence < 0.4) {
    const reasons: Record<string, string> = {
      'no-background-above': 'фон над головой не найдён (тёмные волосы на тёмном фоне)',
      'dark-subject-on-dark-background': 'субъект и фон почти одной яркости',
      'no-consistent-edge': 'граница волос не найдена однозначно',
      'high-noise-tolerance': 'сильный шум мешает определить границу',
      'weak-contrast': 'слабый контраст между волосами и фоном',
      'columns-disagree': 'колонки дают разные границы',
      'invalid-buffer': 'недоступен пиксельный буфер',
    };
    const explained = silhouette.notes
      .map((note) => reasons[note])
      .filter((text): text is string => Boolean(text));

    return {
      ...estimate,
      notes: [...estimate.notes, ...silhouette.notes, 'silhouette-rejected'],
      silhouette: {
        decision: 'rejected',
        reason:
          explained.length > 0 ? explained.join(', ') : silhouette.notes.join(', ') || 'не найдена граница',
      },
      confidence: {
        value: estimate.confidence.value,
        reason:
          explained.length > 0
            ? `Граница волос не определена: ${explained.join(', ')}. Высота головы оценена по landmarks.`
            : 'Граница волос не найдена однозначно, высота головы оценена по landmarks.',
      },
    };
  }

  const headTopY = silhouette.headTopY;
  const headHeight = estimate.headTopY + estimate.headHeight - headTopY;

  /**
   * Plausibility guard — the fix for the "head = 44 %" bug.
   *
   * The scan can lock onto the *hairline* instead of the top of the hair: when
   * the hair is as dark as the background, the hair reads as background and the
   * first transition it meets is the forehead. The result is a "head" that is
   * only as tall as the face mesh, which then fails a perfectly good photo.
   *
   * A head is never shorter than the face mesh (the mesh ends at the forehead),
   * so anything below `faceMesh × 1.08` is a measurement artefact, not a head.
   */
  const minimumPlausible = estimate.estimators.faceMesh * MIN_HEAD_OVER_FACE_MESH;
  /**
   * The other end of the band: a head can be at most ~1.8× the forehead→chin
   * mesh. A silhouette that claims more has jumped to something else — a hat
   * brim, a shoulder, a hair halo, or a completely broken landmark set — and must
   * not be trusted.
   */
  const maximumPlausible = estimate.estimators.faceMesh * MAX_HEAD_OVER_FACE_MESH;
  if (headHeight > maximumPlausible) {
    return {
      ...estimate,
      notes: [...estimate.notes, ...silhouette.notes, 'silhouette-implausible-height', 'silhouette-rejected'],
      silhouette: {
        decision: 'rejected',
        reason: 'силуэт дал высоту выше правдоподобной (faceMesh × 1.8)',
        headHeight,
      },
      confidence: {
        value: Math.min(estimate.confidence.value, 0.4),
        reason:
          'Силуэт дал высоту головы, несовместимую с размерами лица: результат требует проверки, а не вердикта.',
      },
    };
  }
  if (headHeight < minimumPlausible) {
    return {
      ...estimate,
      notes: [...estimate.notes, ...silhouette.notes, 'silhouette-hairline-lock', 'silhouette-rejected'],
      silhouette: {
        decision: 'rejected',
        reason: 'силуэт совпал с линией роста волос (ниже faceMesh × 1.08)',
        headHeight,
      },
      confidence: {
        value: estimate.confidence.value,
        reason:
          'Граница волос совпала с линией роста волос: высота головы оценена по landmark-пропорциям, а не по силуэту.',
      },
    };
  }

  const notes = [...estimate.notes, ...silhouette.notes];
  // Volume above the hairline (long hair, an updo or headwear) makes the
  // visible contour sit well above the mesh extrapolation.
  if (options.meshTopY !== undefined && options.meshTopY - headTopY > headHeight * 0.15) {
    notes.push('volume-above-hairline');
  }

  const reasonParts: string[] = [];
  if (silhouette.confidence < 0.6) {
    reasonParts.push('верхняя граница определена по контрасту с фоном, возможна погрешность');
  }
  if (notes.includes('volume-above-hairline')) {
    reasonParts.push('над линией волос есть объём (объёмная причёска или головной убор)');
  }

  return {
    ...estimate,
    headTopY,
    headHeight,
    source: 'silhouette',
    silhouette: {
      decision: 'accepted',
      reason: `контраст ${silhouette.contrast}, колонок ${silhouette.columnsUsed}`,
      headHeight,
    },
    confidence: {
      value: round(clamp(0.55 + silhouette.confidence * 0.4, 0, 0.95), 2),
      reason: reasonParts.length > 0 ? `${reasonParts.join('; ')}.` : undefined,
    },
    notes,
  };
};
